import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, SectionHeading, EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const img = (id) => `https://images.unsplash.com/${id}?q=80&w=1600&auto=format&fit=crop`;

const PATTERN = [
  "md:col-span-7 aspect-[4/3]",
  "md:col-span-5 aspect-[3/4] md:mt-32",
  "md:col-span-5 aspect-square",
  "md:col-span-7 aspect-[4/3] md:mt-32",
];

const PROJECTS = [
  {
    name: "The Foundry",
    type: "Adaptive Reuse",
    location: "In Progress",
    year: "2026",
    src: img("photo-1541888946425-d81bb19240f5"),
    cls: "md:col-span-5 aspect-[3/4] md:mt-32",
  },
  {
    name: "Gallery House",
    type: "Custom Home",
    location: "Potomac, MD",
    year: "2023",
    src: img("photo-1600607687939-ce8a6c25118c"),
    cls: "md:col-span-5 aspect-square",
  },
  {
    name: "Frame + Form Studio",
    type: "Commercial Build-Out",
    location: "Silver Spring, MD",
    year: "2024",
    src: img("photo-1486406146926-c627a92ad1ab"),
    cls: "md:col-span-7 aspect-[4/3] md:mt-32",
  },
];

const ProjectCard = ({ p, i, num }) => (
  <FadeUp delay={(i % 2) * 0.1} className={PATTERN[i % PATTERN.length]}>
    <a
      href="#contact"
      onClick={(e) => e.preventDefault()}
      className="group relative block w-full h-full overflow-hidden border border-white/15"
      data-testid={`project-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      aria-label={p.name}
    >
      <img
        src={p.src}
        alt={p.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <motion.div
        className="absolute bottom-0 inset-x-0 p-6 md:p-8 flex items-end justify-between translate-y-6 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500"
        style={{ transitionTimingFunction: `cubic-bezier(${EASE.join(",")})` }}
      >
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-ash mb-2">
            {p.type} — {p.location} — {p.year}
          </div>
          <div className="font-display text-2xl md:text-3xl uppercase font-bold tracking-tight">{p.name}</div>
        </div>
        <ArrowUpRight size={28} strokeWidth={1.5} className="shrink-0" />
      </motion.div>
      <div className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase text-paper bg-[#19254D]/80 backdrop-blur-sm px-2 py-1">
        {p.real ? "ETMB Build" : String(num).padStart(2, "0")}
      </div>
    </a>
  </FadeUp>
);

export const Projects = () => {
  const [real, setReal] = useState([]);

  useEffect(() => {
    axios.get(`${API}/photos`, { params: { category: "project" } }).then((r) => setReal(r.data)).catch(() => {});
  }, []);

  const cards = [
    ...real.map((p) => ({
      name: p.title,
      type: p.project_type || "Project",
      location: p.location || "ETMB Design Build",
      year: p.year || "",
      src: `${API}/photos/file/${p.id}`,
      real: true,
    })),
    ...PROJECTS,
  ];

  return (
    <section id="work" className="px-6 md:px-12 py-28 md:py-40" data-testid="projects-section">
      <SectionHeading index="Selected Work" title="Built Proof" testId="projects-heading" />
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        {cards.map((p, i) => (
          <ProjectCard key={p.name + i} p={p} i={i} num={p.real ? 0 : i - real.length + 1} />
        ))}
      </div>
      <FadeUp className="mt-12">
        <p className="text-[10px] tracking-[0.3em] uppercase text-ash" data-testid="projects-placeholder-note">
          Representative work — more completed ETMB projects being documented
        </p>
      </FadeUp>
    </section>
  );
};
